import { useEffect, useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Field } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const init = (l, b) => ({
  principal: (l.principal_cents / 100).toFixed(2), apr: String(l.apr), term_months: String(l.term_months), interest_type: l.interest_type,
  start_date: l.start_date, due_day: String(+l.first_due_date.slice(8, 10)), late_fee_type: l.late_fee_type,
  late_fee_value: l.late_fee_type === "flat" ? (l.late_fee_value / 100).toFixed(2) : String(l.late_fee_value),
  grace_days: String(l.grace_days), statement_day: l.statement_day ? String(l.statement_day) : "", notes: l.notes || "",
  borrower_phone: b?.phone || "", borrower_address: b?.address || "",
});

export const EditLoanDialog = ({ open, onOpenChange, loan, locked, onSaved }) => {
  const [f, setF] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    api.get(`/borrowers/${loan.borrower_id}`).then((r) => setF(init(loan, r.data))).catch(() => setF(init(loan, null)));
  }, [open, loan]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const inp = (k, props = {}) => (
    <Input id={k} value={f[k]} onChange={set(k)} className={`num h-10 ${errors[k] ? "border-destructive" : ""}`} data-testid={`edit-loan-${k.replace(/_/g, "-")}-input`} {...props} />
  );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/loans/${loan.id}`, { ...f, statement_day: f.statement_day.trim() });
      toast.success("Loan updated");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      const { message, fields } = apiError(err);
      setErrors(fields);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="edit-loan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit loan {loan.loan_number}</DialogTitle>
          <DialogDescription>Changes are recorded in the loan's activity log and balances are recomputed.</DialogDescription>
        </DialogHeader>
        {f && (
          <form onSubmit={submit} noValidate className="space-y-6" data-testid="edit-loan-form">
            <section>
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">Financial terms {locked && <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground" data-testid="financial-terms-locked"><Lock className="h-3 w-3" /> Locked — payments recorded</span>}</div>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Principal (USD)" name="principal" error={errors.principal}>{inp("principal", { disabled: locked })}</Field>
                <Field label="APR (%)" name="apr" error={errors.apr}>{inp("apr", { disabled: locked })}</Field>
                <Field label="Term (months)" name="term_months" error={errors.term_months}>{inp("term_months", { disabled: locked })}</Field>
                <Field label="Interest type" name="interest_type" error={errors.interest_type}>
                  <Select value={f.interest_type} onValueChange={set("interest_type")} disabled={locked}>
                    <SelectTrigger className="h-10" data-testid="edit-loan-interest-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="amortized">Amortized</SelectItem><SelectItem value="simple">Simple interest</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Start date" name="start_date" error={errors.start_date}>{inp("start_date", { type: "date", disabled: locked })}</Field>
                <Field label="Payment due day" name="due_day" error={errors.due_day} hint="Day of month (1–31)">{inp("due_day", { inputMode: "numeric" })}</Field>
              </div>
            </section>
            <section className="grid gap-4 sm:grid-cols-3">
              <Field label="Late fee type" name="late_fee_type" error={errors.late_fee_type}>
                <Select value={f.late_fee_type} onValueChange={set("late_fee_type")}>
                  <SelectTrigger className="h-10" data-testid="edit-loan-late-fee-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="flat">Flat amount</SelectItem><SelectItem value="percent">Percent of installment</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label={f.late_fee_type === "flat" ? "Fee (USD)" : "Fee (%)"} name="late_fee_value" error={errors.late_fee_value}>{inp("late_fee_value")}</Field>
              <Field label="Grace days" name="grace_days" error={errors.grace_days}>{inp("grace_days")}</Field>
              <Field label="Statement day override" name="statement_day" error={errors.statement_day} hint="1–28 · blank = Settings default">{inp("statement_day", { placeholder: "Default" })}</Field>
              <Field label="Borrower phone" name="borrower_phone">{inp("borrower_phone")}</Field>
              <Field label="Borrower address" name="borrower_address">{inp("borrower_address")}</Field>
            </section>
            <Field label="Notes" name="notes"><Textarea id="notes" rows={2} value={f.notes} onChange={set("notes")} data-testid="edit-loan-notes-input" /></Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-loan-cancel">Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="edit-loan-save">{busy && <Loader2 className="animate-spin" />} Save changes</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
